import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * DeviceContext
 * Contexto global para exponer el tipo de dispositivo y el tamaño de la ventana.
 * - Provee: isDesktop, isMobile, deviceType, width, height.
 */
const DeviceContext = createContext({
  isDesktop: false,
  isMobile: true,
  deviceType: "mobile",
  width: typeof window !== "undefined" ? window.innerWidth : 0,
  height: typeof window !== "undefined" ? window.innerHeight : 0,
});

/**
 * useDevice
 * Hook para consumir el contexto de dispositivo.
 */
export const useDevice = () => {
  const ctx = useContext(DeviceContext);

  // Si no hay provider, hacemos un fallback con window.matchMedia
  if (!ctx || typeof ctx.isDesktop === "undefined") {
    if (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function"
    ) {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      return {
        isDesktop,
        isMobile: !isDesktop,
        deviceType: isDesktop ? "desktop" : "mobile",
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    return {
      isDesktop: false,
      isMobile: true,
      deviceType: "mobile",
      width: 0,
      height: 0,
    };
  }

  return ctx;
};

/**
 * DeviceProvider
 * Proveedor de contexto que escucha cambios de tamaño de ventana y actualiza el estado.
 */
export const DeviceProvider = ({ children }) => {
  const [size, setSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const onResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const value = useMemo(() => {
    const isDesktop = size.width >= 1024;
    return {
      isDesktop,
      isMobile: !isDesktop,
      deviceType: isDesktop ? "desktop" : "mobile",
      width: size.width,
      height: size.height,
    };
  }, [size.width, size.height]);

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
};

export default DeviceProvider;
